import { isCurrentPlanOffThatch } from 'components/member_onboarding_v2/utils'
import { Banner } from 'slab'
import {
  type MemberOnboardingMemberPlanChangesProps,
  type MemberOnboardingRenewalMedicalPlan,
} from 'utils/web_api.schemas'

import RenewalsPlanComparisonHeader from '../RenewalsPlanComparisonHeader/RenewalsPlanComparisonHeader'
import RenewalsPlanComparisonTable, {
  type RowValues,
} from '../RenewalsPlanComparisonTable/RenewalsPlanComparisonTable'

export interface MedicalPlanChangesTableProps {
  planChangeSummary: MemberOnboardingMemberPlanChangesProps;
  hasFamily: boolean;
  thereIsNoNextPlanMessage?: string;
  isRenewals?: boolean;
}

export const MedicalPlanChangesTable = ({
  planChangeSummary,
  hasFamily,
  thereIsNoNextPlanMessage,
  isRenewals = true,
}: MedicalPlanChangesTableProps) => {
  const prevYear = planChangeSummary.current_plan_year
  const nextYear = planChangeSummary.next_plan_year
  const yearOverYearChange = planChangeSummary.year_over_year_change

  const prevYearMedicalPlan = prevYear.medical_plan
  const nextYearMedicalPlan = nextYear.medical_plan

  if (!prevYearMedicalPlan) {
    return null
  }

  let planToUseForHeader: MemberOnboardingRenewalMedicalPlan | null =
    nextYearMedicalPlan || prevYearMedicalPlan
  if (thereIsNoNextPlanMessage) {
    planToUseForHeader = null
  }

  const planRow: RowValues[] = [
    {
      label: 'Plan',
      prevValue: (
        <div className='flex flex-col'>
          <span className='body-sm-bold text-gray-800'>
            {prevYearMedicalPlan?.plan_name}
          </span>
          <span className='body-sm text-gray-500'>
            {prevYearMedicalPlan?.carrier_name}
          </span>
        </div>
      ),
      newValue: nextYearMedicalPlan
        ? (
          <div className='flex flex-col'>
            <span className='body-sm-bold text-gray-800'>
              {nextYearMedicalPlan?.plan_name}
            </span>
            <span className='body-sm text-gray-500'>
              {nextYearMedicalPlan?.carrier_name}
            </span>
          </div>)
        : '',
      yoyChange: undefined,
      increaseIsGreen: false,
      compactRow: true,
    },
  ]

  const medicalPlanRowValues: RowValues[] = [
    {
      label: 'Monthly premium',
      prevValue: prevYearMedicalPlan?.premium_amount.formatted_amount,
      newValue: nextYearMedicalPlan
        ? nextYearMedicalPlan?.premium_amount.formatted_amount
        : 'Unknown',
      yoyChange: nextYearMedicalPlan
        ? yearOverYearChange.medical_amount
        : undefined,
      increaseIsGreen: false,
    },
  ]

  const onThatchMedicalPlanRowValues: RowValues[] = [
    {
      label: hasFamily ? 'Family deductible' : 'Deductible',
      prevValue: hasFamily
        ? prevYearMedicalPlan?.family_deductible
        : prevYearMedicalPlan?.deductible,
      newValue: nextYearMedicalPlan
        ? hasFamily
          ? nextYearMedicalPlan?.family_deductible
          : nextYearMedicalPlan?.deductible
        : 'Unknown',
      yoyChange: nextYearMedicalPlan
        ? hasFamily
          ? yearOverYearChange.medical_feature_change.family_deductible
          : yearOverYearChange.medical_feature_change.deductible
        : undefined,
      increaseIsGreen: false,
    },
    {
      label: hasFamily ? 'Family out of pocket max' : 'Out of pocket max',
      prevValue: hasFamily
        ? prevYearMedicalPlan?.family_out_of_pocket_max
        : prevYearMedicalPlan?.out_of_pocket_max,
      newValue: nextYearMedicalPlan
        ? hasFamily
          ? nextYearMedicalPlan?.family_out_of_pocket_max
          : nextYearMedicalPlan?.out_of_pocket_max
        : 'Unknown',
      yoyChange: nextYearMedicalPlan
        ? hasFamily
          ? yearOverYearChange.medical_feature_change.family_out_of_pocket_max
          : yearOverYearChange.medical_feature_change.out_of_pocket_max
        : undefined,
      increaseIsGreen: false,
    },
  ]

  const offThatchOrMedicare = isCurrentPlanOffThatch(planChangeSummary)
  const planIsNotTheSame =
    nextYear.medical_plan_renewal_type === 'same_carrier_new_plan' ||
    nextYear.medical_plan_renewal_type === 'new_carrier_new_plan'

  const rows = offThatchOrMedicare
    ? medicalPlanRowValues
    : [...medicalPlanRowValues, ...onThatchMedicalPlanRowValues]

  if (thereIsNoNextPlanMessage || planIsNotTheSame) {
    rows.unshift(...planRow)
  }

  const viewFullPlanDetailsUrl = !isRenewals && nextYearMedicalPlan
    ? `/member_onboarding/progress?step=renewal_curr_year_medical_plan_details:${nextYearMedicalPlan.plan_code}`
    : undefined

  return (
    <div className='flex flex-col gap-6 border border-gray-200 rounded-2xl shadow-sm p-4 xs:p-6'>
      <RenewalsPlanComparisonHeader
        plan={planToUseForHeader}
        planType='medical'
        viewFullPlanDetailsUrl={viewFullPlanDetailsUrl}
      />
      {offThatchOrMedicare && (
        <Banner
          title={`You purchased this plan outside of Thatch — reference your plan for ${
            nextYear?.plan_year ? nextYear.plan_year + ' ' : ''
          }details`}
          type='notice'
        />
      )}
      {planIsNotTheSame && (
        <Banner
          title={`Your ${prevYear.plan_year} plan isn't available for ${nextYear.plan_year}.
          We chose the plan that most closely matched.`}
          type='notice'
        />
      )}
      <RenewalsPlanComparisonTable
        prevPlanYear={prevYear.plan_year}
        currPlanYear={nextYear.plan_year}
        rows={rows}
        thereIsNoNextPlanMessage={thereIsNoNextPlanMessage}
        onNavigateToPrevPlanDetailsPage={
          isRenewals && !offThatchOrMedicare
            ? () => {
                window.open(
                  `/member_onboarding/progress?step=renewal_curr_year_medical_plan_details:${prevYearMedicalPlan?.plan_code}`,
                  '_blank'
                )
              }
            : undefined
        }
        onNavigateToCurrPlanDetailsPage={
          isRenewals && nextYearMedicalPlan
            ? () => {
                window.open(
                  `/member_onboarding/progress?step=renewal_next_year_medical_plan_details:${nextYearMedicalPlan.plan_code}`,
                  '_blank'
                )
              }
            : undefined
        }
      />
    </div>
  )
}
